import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Music, PlayCircle, Download, ExternalLink, Youtube } from 'lucide-react';
import Footer from './Footer';

const MindsetShiftSoundtrackPage = () => {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handlePurchase = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: 'mindset_shift_soundtrack',
          origin_url: window.location.origin
        })
      });
      
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-red-900">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-sm py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => navigate('/')}
            className="text-white hover:text-red-500 transition-colors flex items-center gap-2"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-5xl mx-auto text-center mb-16">
          <div className="mb-8">
            <Music className="w-20 h-20 mx-auto text-yellow-400 mb-4" />
            <h1 className="text-5xl md:text-7xl font-black text-white mb-4">
              MINDSET SHIFT
            </h1>
            <p className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2">
              The Official Soundtrack
            </p>
            <p className="text-xl text-gray-300 mb-4">
              Music inspired by the award-nominated book
            </p>
            <p className="text-lg text-gray-400">
              By Egypt Young | Created by Audrey Egypt Young
            </p>
            <p className="text-sm text-gray-500 mt-2">
              An eBlackBookListings Publications release
            </p>
          </div>

          {/* Album Cover */}
          <div className="max-w-md mx-auto mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/ujuixs3p_MINDSET%20SHIFT%20Soundtrack%20Cover.png"
              alt="Mindset Shift - The Official Soundtrack"
              className="w-full aspect-square rounded-2xl shadow-2xl"
            />
          </div>

          {/* Experience Messaging */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-8 border border-white/20">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              The Window to the Soul
            </h2>
            <p className="text-lg text-gray-200 mb-4 leading-relaxed">
              This soundtrack is the emotional engine of the complete Mindset Shift eCoSystem — 
              where mindset becomes movement and movement creates opportunity.
            </p>
            <p className="text-gray-300 mb-6">
              11 powerful tracks (10 R&B + 1 Bonus Rap) that open your heart to transformation.
            </p>

            {/* What's Included */}
            <div className="bg-green-500/20 border-2 border-green-500 rounded-xl p-6">
              <h3 className="text-2xl font-bold text-green-400 mb-4 flex items-center gap-2">
                🎁 What You Get When You Own It:
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="bg-green-500 rounded-full p-1 mt-1">
                    <Music className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Full Album (11 Tracks)</p>
                    <p className="text-gray-200 text-sm">High-quality MP3 files for your personal library</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-yellow-500 rounded-full p-1 mt-1">
                    <Download className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">EXCLUSIVE Official Lyric Sheet (PDF)</p>
                    <p className="text-gray-200 text-sm">Complete lyrics for all 11 tracks - NOT available on streaming platforms!</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-purple-500 rounded-full p-1 mt-1">
                    <Download className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Lifetime Access</p>
                    <p className="text-gray-200 text-sm">Download once, keep forever - no subscription needed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-8">
            {/* Stream */}
            <a
              href="https://distrokid.com/hyperfollow/egyptyoung/mindset-shift-the-official-soundtrack-2"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6 rounded-xl transition-all hover:scale-105">
                <PlayCircle className="w-6 h-6 mr-2" />
                Stream on All Platforms
              </Button>
            </a>

            {/* Buy */}
            <Button 
              onClick={handlePurchase}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black text-lg py-6 rounded-xl transition-all hover:scale-105 font-bold"
            >
              <Download className="w-6 h-6 mr-2" />
              Own It Forever - $9.99
            </Button>
          </div>

          <p className="text-sm text-gray-400 mb-12">
            💡 Streaming pays pennies. Direct purchase supports the artist and gives you lifetime access.
          </p>

          {/* NEW SINGLE: My Momma Is My Hero */}
          <div className="bg-gradient-to-br from-pink-900/40 to-black/60 backdrop-blur-md rounded-2xl p-8 mb-16 border border-pink-500/30">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0 w-32 h-32 bg-gradient-to-br from-pink-600 to-amber-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Music className="w-16 h-16 text-white" />
              </div>
              <div className="text-center md:text-left flex-1">
                <p className="text-pink-400 text-sm font-bold tracking-wider mb-1">NEW SINGLE</p>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">My Momma Is My Hero</h3>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  A heartfelt tribute song honoring the love, strength, and legacy of mothers.
                  Perfect for Mother's Day tributes, videos, and social media posts.
                </p>
                <a
                  href="https://buy.stripe.com/cNibJ3dsc1081YUdV6ebu04"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-gradient-to-r from-pink-600 to-amber-600 hover:from-pink-700 hover:to-amber-700 text-white font-bold text-lg px-8 py-4 rounded-full transition-all hover:scale-105" data-testid="buy-momma-single">
                    <Download className="w-5 h-5 mr-2" />
                    Buy Single – $4.99
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Why Own It */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <Download className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-white font-bold text-lg mb-2">Exclusive Lyrics</h3>
              <p className="text-gray-300 text-sm">
                Official lyric sheet for all 11 tracks - ONLY available with direct purchase!
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <Music className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-white font-bold text-lg mb-2">High Quality MP3</h3>
              <p className="text-gray-300 text-sm">
                Premium audio quality for your personal library. No streaming required.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <ExternalLink className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-white font-bold text-lg mb-2">Support the Artist</h3>
              <p className="text-gray-300 text-sm">
                1 purchase = 2,000+ streams worth of revenue + you get the exclusive lyrics!
              </p>
            </div>
          </div>
        </div>

        {/* Jersey Mindset Auntie Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-red-900/50 to-black/50 backdrop-blur-md rounded-2xl p-8 border border-red-500/30">
            <div className="flex items-center gap-4 mb-6">
              <Youtube className="w-12 h-12 text-red-500" />
              <div>
                <h2 className="text-3xl font-black text-white">
                  Jersey Mindset Auntie
                </h2>
                <p className="text-gray-300">
                  Your Digital Mentor • Mindset Before Money
                </p>
              </div>
            </div>
            
            <p className="text-gray-200 mb-6 leading-relaxed">
              Meet your trusted guide on the journey to transformation. Jersey Mindset Auntie delivers 
              truth before tactics, guidance before grind, and helps you stop leaving money on the table.
            </p>

            <a
              href="https://youtube.com/shorts/T-RXF5lkF7A?si=rYNc54mNvTpOxHgr"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                <Youtube className="w-5 h-5 mr-2" />
                Watch Video Series
              </Button>
            </a>
          </div>
        </div>

        {/* Connection to Full eCoSystem */}
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-purple-900/30 backdrop-blur-md rounded-2xl p-8 border border-purple-500/30">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              This is Just the Beginning
            </h2>
            <p className="text-gray-200 mb-6 leading-relaxed">
              The soundtrack opens your heart. The complete Mindset Shift eCoSystem transforms your life — 
              with the book, workbook, journal, affirmations, and video mentorship.
            </p>
            <Button 
              onClick={() => navigate('/mindset-shift-ecosystem')}
              variant="outline"
              className="border-purple-500 text-purple-300 hover:bg-purple-900/50"
            >
              Explore the Full eCoSystem
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MindsetShiftSoundtrackPage;
